import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AcademicCapIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  BanknotesIcon,
  BeakerIcon,
  ChartBarIcon,
  DocumentCheckIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  BookmarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { useAppContext } from "../../functions/ContextAPI";
import ScoreCard from "../../components/ScoreCard";
import ScoreInput from "../../components/ScoreInput";
import RecommendationCard from "../../components/RecommendationCard";
import ConfirmationModal from "../../components/ConfirmationModal";
import TemplateGrid from "../../components/TemplateGrid";
import TemplateScoreModal from "../../components/TemplateScoreModal";
import { SkeletonCard } from "../../components/LoadingStates";
import useScoreForm from "../../hooks/useScoreForm";
import useCalculation from "../../hooks/useCalculation";
import useTemplateScoring from "../../hooks/useTemplateScoring";
import { calculateAccreditationRecommendation } from "../../utils/accreditationRules";
import { getSelectedProgramme } from "../../service/authService";

/**
 * Dashboard Page Component
 * Main page for template-based score submission
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const context = useAppContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [deficiencies, setDeficiencies] = useState("");
  const [remedies, setRemedies] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Get current programme and user from context
  const currentProgramme = context.selectedProgramme;
  const user = context.user || { name: "Representative", email: "" };

  // Get programme type (default to UG if not specified)
  const programmeType = currentProgramme?.programmeType || currentProgramme?.type || 'undergraduate';

  // Check if programme is selected
  const hasProgramme = !!currentProgramme;

  // Get accreditationId (you may need to adjust this based on your data structure)
  const accreditationId = currentProgramme?.accreditationId || currentProgramme?._id;

  // Debug logging for accreditationId
  useEffect(() => {
    console.log('[Dashboard] AccreditationId:', accreditationId);
    console.log('[Dashboard] CurrentProgramme:', currentProgramme);
  }, [accreditationId, currentProgramme]);

  // Load deficiencies and remedies on mount
  useEffect(() => {
    const loadDeficienciesAndRemedies = async () => {
      if (!accreditationId) return;

      try {
        const { getDeficienciesAndRemedies } = await import("../../service/templateService");
        const data = await getDeficienciesAndRemedies(accreditationId);

        setDeficiencies(data.deficiencies || "");
        setRemedies(data.remedies || "");
      } catch (error) {
        console.error("Error loading deficiencies and remedies:", error);
        // Don't show error toast - it's okay if they haven't been saved yet
      }
    };

    loadDeficienciesAndRemedies();
  }, [accreditationId]);

  // Initialize template scoring hook
  const {
    templates,
    scores: templateScores,
    completionStatus,
    isLoading: isLoadingTemplates,
    isLoadingScores,
    error: templateError,
    updateScores,
    saveScores,
    submitAllScores,
    getTemplateStatus,
    getOverallStatus,
    refreshTemplates,
    refreshScores,
  } = useTemplateScoring({
    accreditationId,
    programmeMode: programmeType,
    templateType: null, // null means both core and supplementary
    debounceDelay: 3000, // 3 second debounce
    enableAutoSave: true // enable auto-save
  });

  // Load selected programme from localStorage if not in context
  useEffect(() => {
    if (!context.selectedProgramme) {
      const storedProgramme = getSelectedProgramme();
      if (storedProgramme) {
        console.log("Loading selected programme from localStorage:", storedProgramme);
        context.setSelectedProgramme(storedProgramme);
      }
    }
  }, [context]);

  // Redirect to settings if no programme selected after check
  // Add delay to allow context to initialize from localStorage
  useEffect(() => {
    const checkProgramme = setTimeout(() => {
      if (!hasProgramme) {
        toast.warning("Please select a programme to continue");
        setTimeout(() => {
          navigate("/settings");
        }, 1500);
      }
    }, 1000); // Wait 1 second for context to load

    return () => clearTimeout(checkProgramme);
  }, [hasProgramme, navigate]);

  // Show template error if any
  useEffect(() => {
    if (templateError) {
      console.error("Template error:", templateError);
      toast.error("Failed to load templates. Please try again.");
    }
  }, [templateError]);

  // Initialize score form hook
  const {
    scores,
    errors,
    touched,
    isValid,
    isDirty,
    scoreFields,
    updateScore,
    getScoreSummary,
    areAllFieldsFilled,
  } = useScoreForm(
    {
      academicContent: context.scores.academicContent || "",
      staffing: context.scores.staffing || "",
      physicalFacilities: context.scores.physicalFacilities || "",
      library: context.scores.library || "",
      funding: context.scores.funding || "",
      researchCollaboration: context.scores.researchCollaboration || "",
      tracerEmployer: context.scores.tracerEmployer || "",
    },
    handleAutoSave,
    3000 // 3 second debounce
  );

  // Initialize calculation hook
  const {
    isCalculating,
    calculationResult,
    quickCalculate,
    fullCalculate,
    resetCalculation,
  } = useCalculation(currentProgramme);

  // Handle template card click
  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setShowTemplateModal(true);
  };

  // Handle template modal close
  const handleTemplateModalClose = () => {
    setShowTemplateModal(false);
    setSelectedTemplate(null);
  };

  // Handle template score save
  const handleTemplateSave = async (isDraft, scores) => {
    try {
      setIsSaving(true);

      // Get user from localStorage (more reliable than context)
      const userFromStorage = JSON.parse(localStorage.getItem('representativeUser') || 'null');
      const tokenString = localStorage.getItem('authToken');

      // Decode token to get representative info
      let decodedToken = null;
      if (tokenString) {
        try {
          // Decode JWT token (without verification, just to read payload)
          const base64Url = tokenString.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          decodedToken = JSON.parse(jsonPayload);
        } catch (e) {
          console.error('Failed to decode token:', e);
        }
      }

      // Get the representativeId - use panelId for panel access, or accreditationId for direct access
      const representativeId =
        userFromStorage?.panelId ||
        userFromStorage?.accreditationId ||
        userFromStorage?._id ||
        userFromStorage?.id ||
        decodedToken?.panelId ||
        decodedToken?.accreditationId ||
        context.user?.panelId ||
        context.user?.accreditationId ||
        context.user?._id ||
        context.user?.id;

      const programmeId = currentProgramme?.programmeId || currentProgramme?.programme?._id || currentProgramme?.id || currentProgramme?._id;

      console.log('[handleTemplateSave] Debug info:', {
        representativeId,
        accreditationId,
        programmeId,
        selectedTemplate: selectedTemplate?._id,
        currentProgramme,
        userFromStorage,
        decodedToken,
        contextUser: context.user
      });

      if (!representativeId || !accreditationId || !programmeId || !selectedTemplate) {
        console.error('[handleTemplateSave] Missing fields:', {
          hasRepresentativeId: !!representativeId,
          hasAccreditationId: !!accreditationId,
          hasProgrammeId: !!programmeId,
          hasSelectedTemplate: !!selectedTemplate
        });
        toast.error("Missing required information. Please try again.");
        setIsSaving(false);
        return;
      }

      // Transform scores into itemScores object format required by backend
      // Backend expects an object where keys are itemIds and values are score objects
      const itemScores = {};

      selectedTemplate.items.forEach(item => {
        const itemId = item._id;
        const enteredValue = scores[itemId];
        // Only treat explicitly null values as N/A (when user checks "Not Applicable")
        // Empty/undefined values mean the item hasn't been scored yet
        const isNotApplicable = enteredValue === null;

        console.log(`[Item ${item.name}] enteredValue:`, enteredValue, 'isNotApplicable:', isNotApplicable);

        itemScores[itemId] = {
          itemId: itemId,
          itemName: item.name,
          maxScore: item.score || 0,
          enteredValue: isNotApplicable ? 0 : parseFloat(enteredValue) || 0,
          isNotApplicable: isNotApplicable,
        };
      });

      console.log('[handleTemplateSave] Transformed itemScores:', itemScores);

      // Import the saveTemplateScores function
      const { saveTemplateScores } = await import("../../service/templateService");

      // Save to backend with correct format
      await saveTemplateScores({
        representativeId,
        accreditationId,
        programmeId,
        templateId: selectedTemplate._id,
        itemScores,
        status: isDraft ? "draft" : "submitted",
      });

      setLastSaved(new Date());

      if (!isDraft) {
        toast.success("Scores submitted successfully!");
      } else {
        toast.success("Draft saved successfully!");
      }

      // Refresh templates and scores
      await refreshScores();

      // Close modal if not draft
      if (!isDraft) {
        handleTemplateModalClose();
      }
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(error.message || "Failed to save scores. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save handler (legacy - kept for backward compatibility)
  async function handleAutoSave(scores) {
    try {
      setIsSaving(true);
      setLastSaved(new Date());
      console.log("Auto-saved scores:", scores);
    } catch (error) {
      console.error("Auto-save failed:", error);
    } finally {
      setIsSaving(false);
    }
  }

  // Handle save draft
  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      toast.info("Saving draft...");

      // Simulate API call - replace with actual API
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setLastSaved(new Date());
      context.setDraftStatus(true);
      toast.success("Draft saved successfully!");
    } catch (error) {
      console.error("Save draft failed:", error);
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle calculate and preview
  const handleCalculatePreview = async () => {
    if (!areAllFieldsFilled()) {
      toast.warning("Please fill in all score fields before calculating.");
      return;
    }

    if (!isValid) {
      toast.error("Please fix validation errors before calculating.");
      return;
    }

    try {
      const result = await quickCalculate(scores);
      if (result) {
        toast.success("Calculation complete!");
      }
    } catch (error) {
      console.error("Calculation failed:", error);
      toast.error("Failed to calculate scores. Please try again.");
    }
  };

  // Handle submit accreditation
  const handleSubmit = () => {
    // Use dynamic template-based validation
    const overallStatus = getOverallStatus();

    if (!overallStatus.isComplete) {
      // Find which templates are incomplete
      const incompleteTemplates = templates.filter((template) => {
        const templateStatus = getTemplateStatus(template._id);
        return !templateStatus.isComplete;
      }).map(t => t.title);

      toast.warning(
        `Please complete all templates before submitting. Incomplete: ${incompleteTemplates.join(", ")} (${overallStatus.completed}/${overallStatus.total} completed)`,
        { autoClose: 5000 }
      );
      return;
    }

    setShowConfirmModal(true);
  };

  // Handle confirmed submission
  const handleConfirmedSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Show initial toast
      toast.info("Submitting accreditation...", { autoClose: false, toastId: 'submitting' });

      // Use the template scoring submit function
      const success = await submitAllScores();

      // Close toast
      toast.dismiss('submitting');

      if (success) {
        // Close modal after successful submission
        setShowConfirmModal(false);

        // Store submission status in context
        context.setDraftStatus(false);
        toast.success("Accreditation submitted successfully!");

        // Navigate to results page after a brief delay
        setTimeout(() => {
          navigate("/results", {
            state: {
              submitted: true,
              programmeDetails: {
                programmeName: currentProgramme?.programmeName,
                institutionName: currentProgramme?.institutionName,
                faculty: currentProgramme?.faculty,
                department: currentProgramme?.department,
              },
              submittedAt: new Date().toISOString(),
            }
          });
        }, 1000);
      }
    } catch (error) {
      console.error("Submission failed:", error);
      toast.dismiss('submitting');
      toast.error(error.message || "Failed to submit accreditation. Please try again.");
      // Keep modal open on error so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get overall completion status
  const overallStatus = getOverallStatus();

  // Add default values for safety
  const safeOverallStatus = {
    completedTemplates: overallStatus?.completed || 0,
    totalTemplates: overallStatus?.total || 0,
    overallCompletionPercentage: overallStatus?.percentage || 0,
    canSubmit: overallStatus?.isComplete || false,
  };

  // Get score summary (kept for legacy code)
  const scoreSummary = getScoreSummary();

  // Calculate recommendation based on programme type and scores
  const getRecommendation = useCallback(() => {
    if (!scoreSummary.isComplete) {
      return null;
    }

    // Use calculation result if available, otherwise calculate based on rules
    if (calculationResult?.recommendation?.text) {
      return calculationResult.recommendation.text;
    }

    const result = calculateAccreditationRecommendation(scores, programmeType);
    return result.recommendation;
  }, [scores, programmeType, scoreSummary.isComplete, calculationResult]);

  // Get current recommendation
  const currentRecommendation = getRecommendation();

  // Core areas (kept for legacy code)
  const coreAreas = scoreFields.filter((field) =>
    ["academicContent", "staffing", "physicalFacilities", "library"].includes(
      field.key
    )
  );

  // Supplementary areas (kept for legacy code)
  const supplementaryAreas = scoreFields.filter((field) =>
    ["funding", "researchCollaboration", "tracerEmployer"].includes(field.key)
  );

  // Show loading/redirect message if no programme
  if (!hasProgramme) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-700 font-semibold">No programme selected</p>
          <p className="text-gray-500 text-sm mt-2">Redirecting to programme selection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Accreditation Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Submit evaluation scores for programme accreditation
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {isSaving && (
              <div className="flex items-center text-sm text-blue-600">
                <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                Saving...
              </div>
            )}
            {lastSaved && !isSaving && (
              <div className="text-sm text-gray-500">
                Last saved: {lastSaved.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Programme Information */}
      {currentProgramme && (
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 p-6 shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1">
              <div className="p-3 bg-blue-600 rounded-lg">
                <AcademicCapIcon className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
                    ✓ SELECTED PROGRAMME
                  </span>
                </div>
                <h2 className="text-xl font-bold text-blue-900">
                  {currentProgramme.programmeName}
                </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                <div>
                  <p className="text-xs text-blue-700 font-medium">
                    Institution
                  </p>
                  <p className="text-sm text-blue-900 font-semibold mt-0.5">
                    {currentProgramme.institutionName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-700 font-medium">Faculty</p>
                  <p className="text-sm text-blue-900 font-semibold mt-0.5">
                    {currentProgramme.faculty || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-700 font-medium">
                    Department
                  </p>
                  <p className="text-sm text-blue-900 font-semibold mt-0.5">
                    {currentProgramme.department || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-700 font-medium">
                    Programme Type
                  </p>
                  <p className="text-sm text-blue-900 font-semibold mt-0.5">
                    {currentProgramme.programmeType || "N/A"}
                  </p>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Current User Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold">
              {user.name?.charAt(0) || "R"}
            </div>
            <div>
              <p className="text-sm text-gray-600">Representative</p>
              <p className="text-lg font-semibold text-gray-900">{user.name}</p>
              {user.email && (
                <p className="text-sm text-gray-500">{user.email}</p>
              )}
            </div>
          </div>
          {currentProgramme && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Selected Programme</p>
              <p className="text-sm font-semibold text-blue-700">{currentProgramme.programmeName}</p>
              <p className="text-xs text-gray-600">{currentProgramme.institutionName}</p>
            </div>
          )}
        </div>
      </div>

      {/* Read-Only Banner for Stage 6 (Submitted) */}
      {currentProgramme?.accreditationData?.stage === 6 && (
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl shadow-lg p-5 text-white mb-6">
          <div className="flex items-center space-x-3">
            <CheckCircleIcon className="h-8 w-8 text-white flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-1">Accreditation Submitted</h3>
              <p className="text-sm text-green-100">
                This accreditation has been submitted and is now in read-only mode. You can view scores and details but cannot make changes.
              </p>
            </div>
            <button
              onClick={() => navigate("/results")}
              className="px-4 py-2 bg-white text-green-700 rounded-lg font-medium text-sm hover:bg-green-50 transition-colors duration-200 flex items-center space-x-2"
            >
              <DocumentCheckIcon className="h-4 w-4" />
              <span>View Results</span>
            </button>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-5 text-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-blue-100 text-sm">Template Completion</p>
            <p className="text-2xl font-bold">
              {safeOverallStatus.completedTemplates} / {safeOverallStatus.totalTemplates} Templates
            </p>
          </div>
          <div className="text-right">
            <p className="text-blue-100 text-sm">Overall Progress</p>
            <p className="text-2xl font-bold">
              {safeOverallStatus.overallCompletionPercentage}%
            </p>
            <p className="text-sm font-semibold text-blue-200">
              {safeOverallStatus.canSubmit ? "Ready to Submit" : "In Progress"}
            </p>
          </div>
        </div>
        <div className="w-full bg-blue-800 bg-opacity-50 rounded-full h-3">
          <div
            className="bg-white h-3 rounded-full transition-all duration-500"
            style={{
              width: `${safeOverallStatus.overallCompletionPercentage}%`,
            }}
          />
        </div>
      </div>

      {/* Template Scoring Section */}
      {isLoadingTemplates || isLoadingScores ? (
        <div className="space-y-6">
          {/* Skeleton Header */}
          <div className="mb-4">
            <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-100 rounded animate-pulse"></div>
          </div>

          {/* Skeleton Template Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        </div>
      ) : templates && templates.length > 0 ? (
        <TemplateGrid
          templates={templates}
          scores={templateScores}
          onTemplateClick={handleTemplateClick}
        />
      ) : (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-yellow-900 mb-2">
            No Templates Available
          </h3>
          <p className="text-yellow-800">
            No active scoring templates found for this programme type ({programmeType}).
            Please contact the administrator.
          </p>
        </div>
      )}

      {/* Completion Status */}
      {safeOverallStatus.canSubmit && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 animate-fadeIn">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-green-600 rounded-lg">
              <DocumentCheckIcon className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-green-900">
                All Templates Completed!
              </h3>
              <p className="text-green-800 text-sm mt-1">
                You have completed all scoring templates. You can now submit your accreditation for review.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Deficiencies and Remedies Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
            <h2 className="text-xl font-bold text-gray-900">
              Deficiencies and Remedies
            </h2>
          </div>
          <p className="text-sm text-gray-600">
            Document identified deficiencies and proposed remedial actions for the programme
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Deficiencies */}
          <div>
            <label htmlFor="deficiencies" className="block text-sm font-semibold text-gray-700 mb-2">
              Identified Deficiencies
              <span className="text-gray-500 font-normal ml-1">(Issues found during evaluation)</span>
            </label>
            <textarea
              id="deficiencies"
              value={deficiencies}
              onChange={(e) => setDeficiencies(e.target.value)}
              placeholder="Document all identified deficiencies, gaps, and areas of concern...&#10;&#10;Example:&#10;- Inadequate laboratory equipment for practical sessions&#10;- Insufficient library resources in the programme area&#10;- Staff shortage in core areas"
              rows={10}
              readOnly={currentProgramme?.accreditationData?.stage === 6}
              className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-y min-h-[200px] text-sm ${currentProgramme?.accreditationData?.stage === 6 ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                {deficiencies.length} characters
              </p>
              {deficiencies.length > 0 && (
                <button
                  onClick={() => setDeficiencies("")}
                  className="text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Remedies */}
          <div>
            <label htmlFor="remedies" className="block text-sm font-semibold text-gray-700 mb-2">
              Proposed Remedies
              <span className="text-gray-500 font-normal ml-1">(Recommended solutions)</span>
            </label>
            <textarea
              id="remedies"
              value={remedies}
              onChange={(e) => setRemedies(e.target.value)}
              placeholder="Document proposed remedial actions and solutions...&#10;&#10;Example:&#10;- Procure additional laboratory equipment within 6 months&#10;- Subscribe to relevant academic databases and journals&#10;- Recruit qualified staff with PhD in core areas"
              rows={10}
              readOnly={currentProgramme?.accreditationData?.stage === 6}
              className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-y min-h-[200px] text-sm ${currentProgramme?.accreditationData?.stage === 6 ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                {remedies.length} characters
              </p>
              {remedies.length > 0 && (
                <button
                  onClick={() => setRemedies("")}
                  className="text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Save Button for Deficiencies and Remedies - Hidden when stage is 6 */}
        {currentProgramme?.accreditationData?.stage !== 6 && (
          <div className="mt-4 flex items-center justify-end space-x-3">
            <button
              onClick={async () => {
                try {
                  setIsSavingNotes(true);
                  const { saveDeficienciesAndRemedies } = await import("../../service/templateService");
                  await saveDeficienciesAndRemedies(accreditationId, deficiencies, remedies);
                  toast.success("Deficiencies and remedies saved successfully!");
                  setLastSaved(new Date());
                } catch (error) {
                  console.error("Error saving deficiencies and remedies:", error);
                  toast.error("Failed to save deficiencies and remedies");
                } finally {
                  setIsSavingNotes(false);
                }
              }}
              disabled={isSavingNotes || (!deficiencies && !remedies)}
              className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSavingNotes ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-white">Saving...</span>
                </>
              ) : (
                <>
                  <BookmarkIcon className="h-4 w-4 text-white" />
                  <span className="text-white">Save Notes</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Scores Summary and Recommendation */}
      {safeOverallStatus.canSubmit && (() => {
        // Calculate total scores from all templates
        let totalEarnedScore = 0;
        let totalMaxScore = 0;

        templates.forEach((template) => {
          const templateScore = templateScores[template._id];
          if (templateScore?.metadata) {
            totalEarnedScore += templateScore.metadata.totalScore || 0;
            totalMaxScore += templateScore.metadata.maxPossibleScore || 0;
          }
        });

        const percentage = totalMaxScore > 0 ? (totalEarnedScore / totalMaxScore) * 100 : 0;

        // Calculate recommendation using the new accreditation rules
        const accreditationResult = calculateAccreditationRecommendation(
          templateScores,
          templates,
          programmeType
        );

        const recommendation = accreditationResult.recommendation;
        const recommendationReason = accreditationResult.reason;
        const coreAreas = accreditationResult.details?.coreAreas || [];

        // Determine colors based on recommendation
        let recommendationColor = "";
        let recommendationBg = "";
        let recommendationBorder = "";

        if (recommendation.includes("FULL") || recommendation === "ACCREDITED") {
          recommendationColor = "text-green-800";
          recommendationBg = "bg-green-50";
          recommendationBorder = "border-green-200";
        } else if (recommendation.includes("INTERIM")) {
          recommendationColor = "text-yellow-800";
          recommendationBg = "bg-yellow-50";
          recommendationBorder = "border-yellow-200";
        } else {
          recommendationColor = "text-red-800";
          recommendationBg = "bg-red-50";
          recommendationBorder = "border-red-200";
        }

        return (
          <div className={`rounded-xl border-2 ${recommendationBorder} ${recommendationBg} p-6 shadow-lg`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Accreditation Summary</h3>
                <p className="text-sm text-gray-600">Overall scoring summary and recommendation</p>
              </div>
              <div className={`px-4 py-2 rounded-lg ${recommendationBg} border-2 ${recommendationBorder}`}>
                <p className="text-xs font-medium text-gray-600">RECOMMENDATION</p>
                <p className={`text-lg font-bold ${recommendationColor} mt-1`}>{recommendation}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Total Score */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase">Total Score</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {totalEarnedScore.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">out of {totalMaxScore.toFixed(2)}</p>
              </div>

              {/* Percentage */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase">Percentage</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {percentage.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">overall score</p>
              </div>

              {/* Templates Completed */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase">Templates</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {safeOverallStatus.completedTemplates}/{safeOverallStatus.totalTemplates}
                </p>
                <p className="text-xs text-gray-500 mt-1">completed</p>
              </div>

              {/* Recommendation Badge */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase">Status</p>
                <p className={`text-lg font-bold ${recommendationColor} mt-1`}>
                  {recommendation.split(' ')[0]}
                </p>
                <p className="text-xs text-gray-500 mt-1">accreditation</p>
              </div>
            </div>

            {/* Recommendation Details */}
            <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-sm text-gray-700 mb-3">
                <span className="font-semibold">Assessment:</span>{" "}
                {recommendationReason}
              </p>

              {/* Core Area Breakdown */}
              {coreAreas.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Core Area Performance</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {coreAreas.map((area, index) => (
                      <div key={index} className="text-xs">
                        <span className="font-medium text-gray-700">{area.name}:</span>{" "}
                        <span className={`font-semibold ${
                          area.percentage >= 70 ? 'text-green-600' :
                          area.percentage >= 60 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {area.percentage.toFixed(1)}%
                        </span>
                        <span className="text-gray-500 ml-1">
                          ({area.score?.toFixed(1) || 0}/{area.max?.toFixed(1) || 0})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Supplementary Area Breakdown */}
              {(() => {
                const supplementaryAreas = accreditationResult.details?.supplementaryAreas || [];
                return supplementaryAreas.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Supplementary Area Performance</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {supplementaryAreas.map((area, index) => (
                        <div key={index} className="text-xs">
                          <span className="font-medium text-gray-700">{area.name}:</span>{" "}
                          <span className={`font-semibold ${
                            area.percentage >= 70 ? 'text-green-600' :
                            area.percentage >= 60 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {area.percentage.toFixed(1)}%
                          </span>
                          <span className="text-gray-500 ml-1">
                            ({area.score?.toFixed(1) || 0}/{area.max?.toFixed(1) || 0})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {/* Action Buttons */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Info Message */}
          <div className="flex items-start space-x-2 text-sm text-gray-600">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p>
              {!safeOverallStatus.canSubmit
                ? `Complete all ${safeOverallStatus.totalTemplates} templates to enable submission (${safeOverallStatus.completedTemplates}/${safeOverallStatus.totalTemplates} completed)`
                : "All templates completed. You can now submit your accreditation for review."}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center space-x-3 w-full sm:w-auto">
            <button
              onClick={() => {
                refreshTemplates();
                refreshScores();
                toast.info("Templates refreshed");
              }}
              disabled={isLoadingTemplates || isLoadingScores}
              className="flex-1 sm:flex-none px-5 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isLoadingTemplates || isLoadingScores ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>

            {/* Hide Submit button if stage is 6 (already submitted) */}
            {currentProgramme?.accreditationData?.stage !== 6 && (
              <button
                onClick={handleSubmit}
                disabled={!safeOverallStatus.canSubmit || isSubmitting}
                className="flex-1 sm:flex-none px-5 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <PaperAirplaneIcon className="h-4 w-4 text-white" />
                <span className="text-white">Submit Accreditation</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Template Score Modal */}
      {selectedTemplate && (
        <TemplateScoreModal
          isOpen={showTemplateModal}
          onClose={handleTemplateModalClose}
          template={selectedTemplate}
          savedScores={templateScores[selectedTemplate._id]?.scores}
          onSave={handleTemplateSave}
          programmeId={currentProgramme?._id}
          accreditationId={accreditationId}
          programmeType={programmeType}
          isLoading={isSaving}
          isReadOnly={currentProgramme?.accreditationData?.stage === 6}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmedSubmit}
        templates={templates}
        templateScores={templateScores}
        programmeType={programmeType}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default Dashboard;